import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  TextField,
  Typography,
  Card,
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Yup from 'yup';

import { blueGradientButton } from '../../../assets/styles/blueGradientButton.style';
import hidePasswordIcon from '../../../assets/svg/hiddenPasswordIcon.svg';
import showPasswordIcon from '../../../assets/svg/showPasswordIcon.svg';
import { axiosInstance } from '../../../assets/utils/axiosInstance';
import SwitchButtonComponent from '../../../components/whistleblower/twoFactorAuthentication/switchButtonComponent';
import TwoFactorAuthenticationModal from '../../../components/whistleblower/twoFactorAuthentication/twoFactorAuthenticationModal';
import { useAuth } from '../../../contexts/auth/authContext';
import { useToastNotificationContext } from '../../../hooks/global/alert/useToastNotification';
import { UserAccessRoles } from '../../../interfaces/ReportPageAccessRoles';
import RESTAPIError from '../../../interfaces/RESTAPIError';
import customerAuthService from '../../../services/customerAuthService';

const SettingsSecurityUser = () => {
  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();
  const googleOauthUserFlag = localStorage.getItem('is_google_oauth_only');
  const [showPassword, setShowPassword] = useState(true);
  const toggleShowPassword = () => {
    setShowPassword((passwordToggle) => !passwordToggle);
  };
  const { slug } = useAuth();
  const [companySlug, setCompanySlug] = useState<string | null>(slug);
  const [switch2FaState, toggleSwitch2FaState] = useState(false);
  const [isStepOneModalOpen, setIsStepOneModalOpen] = useState(false);
  const [totpFlag, setTotpFlag] = useState(false);
  const [qrCodeLink, setQrCodeLink] = useState('');
  const [userAccessRoles, setUserAccessRoles] = useState<UserAccessRoles>();

  //Fetch Access Role Flags
  useEffect(() => {
    const fetchAccessRoles = async () => {
      const response = await customerAuthService.getAccessRoles();
      setUserAccessRoles(response);
    };
    fetchAccessRoles();
  }, []);

  const handle2FaToggle = (newState: boolean) => {
    toggleSwitch2FaState(newState); // Update the state in the parent component
  };

  //Checks if the user has already configured their TOTP GOOGLE AUTHENTICATOR
  const checkGoogleAuthenticatorFlag = async () => {
    if (userAccessRoles?.is_totp_enabled == true) {
      handle2FaToggle(true);
      setTotpFlag(true);
    }
  };

  const openStepOneModal = async () => {
    if (totpFlag) {
      createToastNotification(
        intl.formatMessage({
          defaultMessage:
            'User has already configured Google Authenticator TOTP!',
        }),
        'error',
      );
    } else {
      const response = await axiosInstance().post('/tenant/totp/activate');
      setQrCodeLink(response.data.details);
      setIsStepOneModalOpen(true);
    }
  };

  const closeStepOneModal = () => {
    setIsStepOneModalOpen(false);
  };

  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);

  useEffect(() => {
    checkGoogleAuthenticatorFlag();
    //ts-ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAccessRoles]);

  //Deactivate 2FA Checker
  useEffect(() => {
    if (switch2FaState == false && totpFlag == true) {
      const deactivateTotp = async () => {
        await axiosInstance().post('/tenant/totp/deactivate');
        createToastNotification('deactivated TOTP Authentication', 'success');
        toggleSwitch2FaState(false);
        setTotpFlag(false);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        window.location.reload();
      };
      deactivateTotp();
    }
    //ts-ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switch2FaState]);

  const updatePasswordForm = useFormik({
    initialValues: {
      updatedPassword: '',
      submit: null,
    },
    validationSchema: Yup.object({
      updatedPassword: Yup.string()
        .max(255)
        .min(
          8,
          intl.formatMessage({
            defaultMessage:
              'This password is too short. It must contain at least 8 characters.',
          }),
        )
        .matches(
          /\d/,
          intl.formatMessage({
            defaultMessage: 'This password must contain at least 1 digit.',
          }),
        )
        .matches(
          /[A-Z]/,
          intl.formatMessage({
            defaultMessage:
              'This password must contain at least 1 uppercase letter.',
          }),
        )
        .matches(
          /[a-z]/,
          intl.formatMessage({
            defaultMessage:
              'This password must contain at least 1 lowercase letter.',
          }),
        )
        .matches(
          /[!@#$%^&*(),.?":{}|<>]/,
          intl.formatMessage({
            defaultMessage: 'This password must contain at least 1 symbol.',
          }),
        ),
    }),
    onSubmit: async (values, helpers) => {
      if (googleOauthUserFlag) {
        helpers.setStatus({ success: false });
        const errorMessage = intl.formatMessage({
          defaultMessage: 'Contact your local System Administrator',
        });
        helpers.setErrors({ submit: errorMessage });
        helpers.setSubmitting(false);
      } else {
        try {
          const response = await customerAuthService.updateUserPassword(
            values.updatedPassword,
            companySlug,
          );
          createToastNotification(response.message, 'success');
          values.updatedPassword = '';
        } catch (error) {
          helpers.setStatus({ success: false });
          const customError = error as RESTAPIError;
          const errorMessage =
            customError.error ||
            intl.formatMessage({
              defaultMessage: 'An error occurred updating your password',
            });
          helpers.setErrors({ submit: errorMessage });
          helpers.setSubmitting(false);
        }
      }
    },
  });

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: '6px',
        border: '1px solid #D6D6D6',
        backgroundColor: '#FFF',
        boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.04);',
      }}
    >
      <List sx={{ position: 'absolute' }}>
        {/* Send Reset Password Email Box */}
        <ListItem>
          <Box
            sx={{
              // border: '1px solid red',
              width: '560px',
              height: 'auto',
              maxHeight: 'auto',
            }}
          >
            <form noValidate onSubmit={updatePasswordForm.handleSubmit}>
              <List sx={{ display: 'flex', flexDirection: 'column' }}>
                <ListItem>
                  <Typography
                    sx={{
                      fontFamily: 'Schibsted Grotesk',
                      fontSize: '18px',
                      fontWeight: 500,
                      lineHeight: '22.22px',
                    }}
                  >
                    <FormattedMessage defaultMessage='Update Password' />
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography
                    sx={{
                      color: 'black',
                      fontWeight: 500,
                      fontSize: '14px',
                      fontFamily: 'Schibsted Grotesk',
                    }}
                  >
                    <FormattedMessage defaultMessage='New Password' />
                  </Typography>
                  <Typography
                    sx={{
                      position: 'absolute',
                      right: '60px',
                      color: 'grey',
                      fontWeight: 600,
                      fontFamily: 'Schibsted Grotesk',
                    }}
                  >
                    <FormattedMessage defaultMessage='Action' />
                  </Typography>
                </ListItem>

                <ListItem
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    alignContent: 'center',
                  }}
                >
                  <TextField
                    hiddenLabel
                    name='updatedPassword'
                    id='form-update-password'
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Enter New Password',
                    })}
                    type={showPassword ? 'password' : 'text'}
                    fullWidth
                    autoComplete='off'
                    onBlur={updatePasswordForm.handleBlur}
                    onChange={updatePasswordForm.handleChange}
                    value={updatePasswordForm.values.updatedPassword}
                    variant='filled'
                    sx={{
                      width: '423px',
                      height: '56px',
                      ...{
                        '& .MuiInputBase-input': {
                          paddingLeft: '20px', // Add left padding to the input text
                        },
                        '& .MuiInputBase-input::placeholder': {
                          paddingLeft: '20px', // Add left padding to the placeholder text
                        },
                        '& .MuiFilledInput-root:after': {
                          border: 'none',
                        },
                        '& .MuiFilledInput-root:before': {
                          borderBottom: 'none',
                        },
                        '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before':
                          {
                            borderBottom: 'none',
                          },
                      },
                      ...(updatePasswordForm.values.updatedPassword && {
                        '& .MuiFilledInput-root': {
                          backgroundColor: '#FFFFFF',
                        },
                        '& .MuiFormLabel-root': {
                          fontFamily: 'Schibsted Grotesk',
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'left',
                          color: '#000000',
                        },
                      }),
                    }}
                    InputProps={{
                      style: {
                        border: updatePasswordForm.values.updatedPassword
                          ? '1px solid rgba(0, 0, 0, 0.5)'
                          : '',
                        borderRadius: '6px',
                        overflow: 'hidden',
                      },
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            onClick={toggleShowPassword}
                            edge='end'
                            sx={{ pr: '19px', color: 'black' }}
                          >
                            {showPassword ? (
                              <img src={hidePasswordIcon} alt='hide-pass'></img>
                            ) : (
                              <img src={showPasswordIcon} alt='show-pass'></img>
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    type='submit'
                    sx={{
                      fontWeight: 600,
                      width: '97px',
                      height: '38px',
                      ml: '16px',
                      mr: '20px',
                      ...blueGradientButton,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Schibsted Grotesk',
                        fontSize: '14px',
                        fontWeight: 600,
                        lineHeight: '17.28px',
                        textAlign: 'center',
                        width: 'auto',
                        textTransform: 'none',
                      }}
                    >
                      <FormattedMessage defaultMessage='Update' />
                    </Typography>
                  </Button>
                </ListItem>
                <ListItem>
                  {updatePasswordForm.touched.updatedPassword &&
                    updatePasswordForm.errors.updatedPassword && (
                      <Typography
                        sx={{
                          color: 'red',
                          fontSize: '12px',
                          ml: '5px',
                        }}
                      >
                        {updatePasswordForm.errors.updatedPassword}
                      </Typography>
                    )}
                </ListItem>
              </List>
            </form>
          </Box>
        </ListItem>

        <div
          style={{
            width: '560px',
            height: '2px',
            backgroundColor: 'rgba(214, 214, 214, 1)',
            border: '1px solid rgba(214, 214, 214, 1)',
            marginLeft: '20px',
          }}
        />

        {/* 2FA Authentication activation Box */}
        <ListItem>
          <Box
            sx={{
              width: '560px',
              height: 'auto',
              maxHeight: 'auto',
              padding: '24px 24px 24px 24px',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Schibsted Grotesk',
                fontWeight: 500,
                fontSize: '18px',
              }}
            >
              <FormattedMessage defaultMessage='Two-Factor Authentication' />
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                mt: '20px',
              }}
            >
              <SwitchButtonComponent
                toggleSwitchState={switch2FaState}
                onToggle={handle2FaToggle}
              />
              <Box style={{ width: '402px', height: '48px' }}>
                <Typography sx={{ color: 'rgba(135, 135, 135, 1)' }}>
                  <FormattedMessage defaultMessage='Setup a Time-based One-Time Password (TOTP) Authentication' />
                </Typography>
              </Box>
            </Box>
            {switch2FaState == true ? (
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Button
                  onClick={openStepOneModal}
                  variant='contained'
                  sx={{
                    backgroundColor: 'rgba(36, 73, 252, 1)',
                    width: '210px',
                    height: '38px',
                    borderRadius: '6px',
                    color: 'white',
                    textTransform: 'none',
                    paddingY: '14px',
                    paddingX: '24px',
                    mt: '20px',
                    '&:hover': {
                      backgroundColor: 'rgba(36, 73, 252, 1)', // Remove hover background color
                    },
                  }}
                >
                  <FormattedMessage defaultMessage='Enable Two-Factor Auth' />
                </Button>
                {totpFlag == true ? (
                  <Typography
                    sx={{
                      color: 'rgba(36, 73, 252, 1)',
                      fontWeight: 500,
                      fontFamily: 'Schibsted Grotesk',
                      ml: '20px',
                      mt: '25px',
                    }}
                  >
                    <FormattedMessage defaultMessage='Enabled' />
                  </Typography>
                ) : (
                  ''
                )}
              </Box>
            ) : (
              ''
            )}
          </Box>
          {/* Modal for Step 1 of 2FA TOTP activation */}
          <TwoFactorAuthenticationModal
            open={isStepOneModalOpen}
            handleClose={closeStepOneModal}
            useFlag='customer'
            qrCodeLink={qrCodeLink}
          />
        </ListItem>
      </List>
    </Card>
  );
};

export default SettingsSecurityUser;
